import React, { useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { breakpoints, mediaQuery, Spacings } from '@styles/index';
import { getPublishDay } from '@unicons-utils/playerHelper';
import Image from '@base/Image';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import { useToggleable as useDrawer } from '@stores/toggleables';
import {
  APP_DRAWER_ID,
  LESSON_PATHNAME
} from '@constants/unacademyIcons/icons.constants';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import { BOLD, EXTRA_SMALL } from '@styles/constants/fonts';

interface LessonThumbnailProps {
  className?: string;
  cover: string;
  slug: string;
  courseSlug: string;
  continueWatching: any;
  duration: number;
  isSubscribed: boolean;
  isFullWidth?: boolean;
  mobileHeight?: string;
  desktopHeight?: string;
  isCompactCard?: boolean;
  isLessonActive?: boolean;
  isComingSoon?: boolean;
  publishedAt?: string | null;
}

type SeekerWrapperType = {
  $visible: boolean;
};

type SeekerType = {
  $width: string;
};

const StyledPlayerIcon = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  transition: transform 400ms ease-in-out;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: 24px;
    width: 24px;
    bottom: 8px;
    left: 8px;
  }
`;

type WrapperType = {
  $isFullWidth: boolean;
  $isSubscribed?: boolean;
  $isCompact: boolean;
  $isLessonActive: boolean;
  $isComingSoon?: boolean;
};

const StyledPlayerWrapper = styled.div<WrapperType>`
  border-radius: ${Spacings.SPACING_1B};
  border: ${({ $isLessonActive }) =>
    $isLessonActive && '1px solid var(--color-i-icon-gradient-3)'};
  width: ${({ $isCompact, $isFullWidth }) =>
    $isCompact ? '152px' : `${$isFullWidth ? '324px' : '300px'}`};
  height: ${({ $isFullWidth }) => ($isFullWidth ? 'auto' : '0%')};
  min-width: ${({ $isCompact }) => ($isCompact ? '152px' : '300px')};
  position: relative;
  cursor: ${({ $isSubscribed, $isComingSoon }) =>
    $isSubscribed && !$isComingSoon ? 'pointer' : 'auto'};
  &:hover ${StyledPlayerIcon} {
    transform: scale(1.1);
  }
  ${mediaQuery.uptoMobile} {
    border-radius: ${Spacings.SPACING_1B};
    width: ${({ $isCompact, $isFullWidth }) =>
      $isCompact ? '152px' : `${$isFullWidth ? '302px' : '125px'}`};
    min-width: ${({ $isCompact }) => ($isCompact ? '152px' : '125px')};
  }
`;

const StyledSeekerWrapper = styled.div<SeekerWrapperType>`
  display: ${({ $visible }) => ($visible ? 'inline-block' : 'none')};
  width: 100%;
  position: absolute;
  bottom: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: hidden;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: 3px;
  }
`;

const StyledSeeker = styled.div<SeekerType>`
  background: var(--color-i-icon-butter);
  height: 4px;
  bottom: 0;
  width: ${({ $width }) => $width || '0%'};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: 3px;
  }
`;

type ThumbnailType = {
  $desktopHeight: string;
  $mobileHeight: string;
  $isSubscribed?: boolean;
  $isCompact?: boolean;
  $isComingSoon?: boolean;
};

const StyledThumbnail = styled(Thumbnail)<ThumbnailType>`
  border-radius: ${({ $isCompact }) => ($isCompact ? '4px' : '8px')};
  opacity: ${({ $isComingSoon }) => $isComingSoon && '0.8'};
  height: ${({ $isCompact, $desktopHeight }) =>
    $desktopHeight || ($isCompact ? '90px' : '172px')};
  width: auto;
  cursor: ${({ $isSubscribed, $isComingSoon }) =>
    $isSubscribed && !$isComingSoon ? 'pointer' : 'auto'};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: ${({ $isCompact, $mobileHeight }) =>
      $mobileHeight || ($isCompact ? '90px' : '72px')};
  }
`;

const StyledTag = styled.div<{ $isCompact?: boolean }>`
  position: absolute;
  bottom: ${({ $isCompact }) =>
    $isCompact ? Spacings.SPACING_2B : Spacings.SPACING_4B};
  left: ${({ $isCompact }) =>
    $isCompact ? Spacings.SPACING_2B : Spacings.SPACING_4B};
  text-transform: uppercase;
  padding: 4px 8px;
  color: var(--color-i-icon-dark-tertiary);
  font-size: ${EXTRA_SMALL};
  font-weight: ${BOLD};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-i-icon-light-tertiary);
  width: auto;
  // margin-bottom: ${Spacings.SPACING_1B};
  text-align: center;
  ${mediaQuery.uptoMobile} {
    bottom: ${Spacings.SPACING_2B};
    left: ${Spacings.SPACING_2B};
  }
`;

const LessonThumbnail: React.FC<LessonThumbnailProps> = ({
  className,
  cover,
  slug,
  courseSlug,
  continueWatching,
  duration,
  isSubscribed,
  isFullWidth,
  mobileHeight,
  desktopHeight,
  isCompactCard,
  isLessonActive,
  isComingSoon,
  publishedAt
}) => {
  const { isMobile } = useWindowSize();

  const { openElement: openDrawer } = useDrawer(APP_DRAWER_ID);

  const playerIcon = useMemo(() => {
    if (isSubscribed && !isComingSoon) {
      return 'play-white-circle.svg';
    }
    return 'lock.svg';
  }, [isSubscribed, isComingSoon]);

  const durationPercentage = useMemo(() => {
    if (continueWatching) {
      return Math.max(1, (continueWatching.watchedDuration / duration) * 100);
    }
    return 0;
  }, [continueWatching, duration]);

  const handleLessonRoute = useCallback(() => {
    if ((isMobile && (!isSubscribed || isComingSoon)) || isMobile) openDrawer();
    if (isComingSoon) return;
    const href = `/icons/courses/${courseSlug}/lessons/${slug}`;

    if (isSubscribed && !isMobile) {
      pushRoute(LESSON_PATHNAME, href, { shallow: true });
    }
  }, [courseSlug, slug, isSubscribed, openDrawer, isMobile, isComingSoon]);

  const isSeekbarVisible = useMemo(
    () => continueWatching && !isCompactCard && isSubscribed && !isComingSoon,
    [continueWatching, isCompactCard, isSubscribed, isComingSoon]
  );

  const comingSoonMessage = useMemo(() => {
    if (publishedAt)
      return `Coming ${isMobile ? '' : 'on'} ${getPublishDay(publishedAt)}`;
    return 'Coming soon';
  }, [publishedAt, isMobile]);

  return (
    <StyledPlayerWrapper
      className={className}
      $isCompact={isCompactCard}
      $isSubscribed={isSubscribed}
      $isComingSoon={isComingSoon}
      $isFullWidth={isFullWidth}
      $isLessonActive={isLessonActive}
      onClick={handleLessonRoute}
    >
      <StyledThumbnail
        imgUrl={
          `${cover}/medium` || '/UN_ICONS_PLACEHOLDERS/lesson-placeholder.png'
        }
        orientation="horizontal"
        borderRadius="8px"
        $isSubscribed={isSubscribed}
        $mobileHeight={mobileHeight}
        $desktopHeight={desktopHeight}
        $isCompact={isCompactCard}
        $isComingSoon={isComingSoon}
      />
      {!isCompactCard && (!isComingSoon || !isSubscribed) && (
        <StyledPlayerIcon>
          <Image src={playerIcon} alt="Lesson" layout="fill" />
        </StyledPlayerIcon>
      )}
      {isComingSoon && isSubscribed && (
        <StyledTag $isCompact={isCompactCard}>{comingSoonMessage}</StyledTag>
      )}

      <StyledSeekerWrapper $visible={isSeekbarVisible}>
        <StyledSeeker $width={`${durationPercentage}%`} />
      </StyledSeekerWrapper>
    </StyledPlayerWrapper>
  );
};

LessonThumbnail.defaultProps = {
  isFullWidth: false,
  isLessonActive: false,
  isComingSoon: false,
  mobileHeight: null,
  desktopHeight: null
};

LessonThumbnail.displayName = 'LessonThumbnail';

export default LessonThumbnail;
